import { v4 as uuidv4 } from 'uuid'
import { supabase } from '@/OS/integrations/supabase/client'
import { globalVFS } from '@/OS/utils/storage/VFS'
import { ensureDir, ensureFile, renamePath } from '@/OS/utils/filesystem'
import { getGuardedVFS } from '@/lib/GuardedVFS'

export type OperatorResult<T> = {
  ok: boolean
  data?: T
  error?: { code?: string; message: string }
}

type VFSLike = {
  save(path: string, content: string): Promise<void>
  load(path: string): Promise<string | null>
  delete(path: string): Promise<void>
  list(path: string): Promise<string[]>
  exists(path: string): Promise<boolean>
  createDirectory(path: string): Promise<void>
}

type Dispatcher = (evt: Event) => boolean

type ActionStatus = 'success' | 'error'
type LogPayload = Record<string, any> | undefined

const LOGGING_KEY = 'os_operator_logging'

// Local in-memory confirmation tokens store
const tokenStore = new Map<string, { type: 'delete' | 'reset'; expiresAt: number }>()

export function createOperator(vfs: VFSLike = globalVFS, dispatcher: Dispatcher = (e) => globalThis.window?.dispatchEvent ? window.dispatchEvent(e) : true) {
  const guardedVFS = getGuardedVFS();

  const isLoggingEnabled = () => {
    try {
      return localStorage.getItem(LOGGING_KEY) === 'true'
    } catch {
      return false
    }
  }

  const logAction = async (action: string, payload: LogPayload, status: ActionStatus) => {
    if (!isLoggingEnabled()) {
      // Still emit a lightweight notify event so users see feedback even when DB logging is off
      dispatcher(new CustomEvent('operator:notify', { detail: { title: 'OS Action', message: `${action} (${status})` } }))
      return
    }
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const userId = sessionData.session?.user?.id
      // The OS supabase types may not include os_operator_logs in all builds.
      // Best-effort log without breaking runtime.
      await (supabase as any)
        .from('os_operator_logs')
        .insert({
          user_id: userId ?? null,
          action,
          details: { ...(payload || {}), status }
        })
      dispatcher(new CustomEvent('operator:notify', { detail: { title: 'OS Action', message: `${action} (${status})` } }))

    } catch {
      // Swallow logging errors; still emit notify event
      dispatcher(new CustomEvent('operator:notify', { detail: { title: 'OS Action', message: `${action} (log error)` } }))
    }
  }

  const result = <T>(ok: boolean, data?: T, error?: { code?: string; message: string }): OperatorResult<T> => ({
    ok,
    ...(data !== undefined ? { data } : {}),
    ...(error ? { error } : {})
  })

  const requireToken = (token: string | undefined, type: 'delete' | 'reset'): OperatorResult<true> | null => {
    if (!token) {
      return result(false, undefined, { code: 'CONFIRMATION_REQUIRED', message: `Confirmation token required for ${type}.` })
    }
    const entry = tokenStore.get(token)
    if (!entry || entry.type !== type || entry.expiresAt < Date.now()) {
      return result(false, undefined, { code: 'CONFIRMATION_INVALID', message: `Confirmation token invalid or expired for ${type}.` })
    }
    tokenStore.delete(token)
    return null
  }

  return {
    // Programmatic window open with no-duplicate behaviour handled by useOS
    async openWindow(appId: string, opts?: { title?: string; route?: string; filePath?: string; fileContent?: string }): Promise<OperatorResult<{ appId: string }>> {
      try {
        const title = opts?.title ?? appId
        dispatcher(new CustomEvent('operator:openWindow', { detail: { appId, title, route: opts?.route, filePath: opts?.filePath, fileContent: opts?.fileContent } }))
        await logAction('openWindow', { appId, ...opts }, 'success')
        dispatcher(new CustomEvent('operator:notify', { detail: { title: 'Opened window', message: `${title}` } }))
        return result(true, { appId })
      } catch (e: any) {
        await logAction('openWindow', { appId, error: String(e?.message || e) }, 'error')
        dispatcher(new CustomEvent('operator:notify', { detail: { title: 'Open window failed', message: String(e?.message || e) } }))
        return result(false, undefined, { message: e?.message || 'Failed to open window' })
      }
    },

    async openModule(route: string): Promise<OperatorResult<{ route: string }>> {
      try {
        dispatcher(new CustomEvent('operator:openWindow', { detail: { appId: 'canxp-module', title: `Module - ${route}`, route, filePath: route } }))
        await logAction('openModule', { route }, 'success')
        return result(true, { route })
      } catch (e: any) {
        await logAction('openModule', { route, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to open module' })
      }
    },

    async writeFile(path: string, data: string): Promise<OperatorResult<{ path: string }>> {
      try {
        const dir = path.includes('\\') ? path.slice(0, path.lastIndexOf('\\')) : path
        await ensureDir(dir)
        // v1 agent guard: if enabled, enforce allowlist for operator-marked write operations
        await guardedVFS.save(path, data)
        await logAction('writeFile', { path, size: data.length }, 'success')
        return result(true, { path })
      } catch (e: any) {
        await logAction('writeFile', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to write file' })
      }
    },

    async readFile(path: string): Promise<OperatorResult<{ path: string; data: string | null }>> {
      try {
        const data = await guardedVFS.load(path)
        await logAction('readFile', { path }, 'success')
        return result(true, { path, data })
      } catch (e: any) {
        await logAction('readFile', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to read file' })
      }
    },

    async listDir(path: string): Promise<OperatorResult<{ path: string; entries: string[] }>> {
      try {
        const exists = await guardedVFS.exists(path)
        if (!exists) {
          await ensureDir(path)
        }
        const entries = await guardedVFS.list(path)
        await logAction('listDir', { path, count: entries.length }, 'success')
        return result(true, { path, entries })
      } catch (e: any) {
        await logAction('listDir', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to list directory' })
      }
    },

    async rename(from: string, to: string): Promise<OperatorResult<{ from: string; to: string }>> {
      try {
        const parent = to.includes('\\') ? to.slice(0, to.lastIndexOf('\\')) : to
        await ensureDir(parent)
        const ok = await renamePath(from, to)
        if (!ok) throw new Error('Rename/move failed')
        await logAction('rename', { from, to }, 'success')
        return result(true, { from, to })
      } catch (e: any) {
        await logAction('rename', { from, to, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to rename' })
      }
    },

    async delete(path: string, token?: string): Promise<OperatorResult<{ path: string }>> {
      const gate = requireToken(token, 'delete')
      if (gate) {
        await logAction('delete', { path, status: 'confirmation_required' }, 'error')
        return result(false, undefined, gate.error)
      }
      try {
        await guardedVFS.delete(path)
        await logAction('delete', { path }, 'success')
        return result(true, { path })
      } catch (e: any) {
        await logAction('delete', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to delete' })
      }
    },

    async setWallpaper(src: string): Promise<OperatorResult<{ src: string }>> {
      try {
        localStorage.setItem('os.wallpaper', src)
        dispatcher(new CustomEvent('operator:setWallpaper', { detail: { src } }))
        await logAction('setWallpaper', { src }, 'success')
        return result(true, { src })
      } catch (e: any) {
        await logAction('setWallpaper', { src, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to set wallpaper' })
      }
    },

    async saveSetting(key: string, val: any): Promise<OperatorResult<{ key: string }>> {
      try {
        localStorage.setItem(`os.settings.${key}`, JSON.stringify(val))
        await logAction('saveSetting', { key }, 'success')
        return result(true, { key })
      } catch (e: any) {
        await logAction('saveSetting', { key, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to save setting' })
      }
    },

    // Idempotent helpers
    async ensureDir(path: string): Promise<OperatorResult<{ path: string }>> {
      try {
        await ensureDir(path)
        await logAction('ensureDir', { path }, 'success')
        return result(true, { path })
      } catch (e: any) {
        await logAction('ensureDir', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to ensure directory' })
      }
    },

    async ensureFile(path: string, defaultContent: string = ''): Promise<OperatorResult<{ path: string }>> {
      try {
        await ensureFile(path, defaultContent)
        await logAction('ensureFile', { path }, 'success')
        return result(true, { path })
      } catch (e: any) {
        await logAction('ensureFile', { path, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to ensure file' })
      }
    },

    // Destructive confirmations
    async confirmDelete(paths: string[]): Promise<OperatorResult<{ token: string; paths: string[] }>> {
      try {
        const ok = globalThis.window?.confirm
          ? window.confirm(`Are you sure you want to delete ${paths.length} item(s)?`)
          : true
        if (!ok) {
          await logAction('confirmDelete', { paths, decision: 'cancel' }, 'error')
          return result(false, undefined, { code: 'CANCELLED', message: 'User cancelled delete confirmation' })
        }
        const token = uuidv4()
        tokenStore.set(token, { type: 'delete', expiresAt: Date.now() + 60_000 })
        await logAction('confirmDelete', { paths }, 'success')
        return result(true, { token, paths })
      } catch (e: any) {
        await logAction('confirmDelete', { paths, error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to confirm delete' })
      }
    },

    async confirmReset(): Promise<OperatorResult<{ token: string }>> {
      try {
        const ok = globalThis.window?.confirm
          ? window.confirm(`Reset will clear settings and may remove data. Continue?`)
          : true
        if (!ok) {
          await logAction('confirmReset', { decision: 'cancel' }, 'error')
          return result(false, undefined, { code: 'CANCELLED', message: 'User cancelled reset' })
        }
        const token = uuidv4()
        tokenStore.set(token, { type: 'reset', expiresAt: Date.now() + 60_000 })
        await logAction('confirmReset', {}, 'success')
        return result(true, { token })
      } catch (e: any) {
        await logAction('confirmReset', { error: String(e?.message || e) }, 'error')
        return result(false, undefined, { message: e?.message || 'Failed to confirm reset' })
      }
    },

    // Logging toggle helper
    setLogging(enabled: boolean) {
      try {
        localStorage.setItem(LOGGING_KEY, enabled ? 'true' : 'false')
      } catch {
        // ignore
      }
    }
  }
}

// Default exported operator bound to the app's VFS and dispatcher
export const operator = createOperator()